// Turning a connector's state into something true that a human can act on.
//
// ⚠️ THE WHOLE POINT IS NOT SAYING "FINE" WHEN IT IS NOT. A biometric
// connector fails quietly: the machine is switched off, the terminals keep
// recording, and every screen carries on showing yesterday's attendance as
// though it were today's. Nothing errors. The only way anybody finds out is
// if this card tells them, so every state here has to earn its reassurance.
//
// In particular there is no state that means "all good" on the strength of
// nothing having gone wrong. `synced` says what it actually knows — recent
// data arrived and nothing is outstanding — and `verified` is reserved for a
// window whose counts have been compared against BioTime itself.

export type ConnectorState =
  | 'never_connected'
  | 'offline'
  | 'catching_up'
  | 'awaiting_identity'
  | 'degraded'
  | 'synced'
  | 'verified';

export type Tone = 'neutral' | 'good' | 'warning' | 'danger';

export interface ConnectorStatus {
  state: ConnectorState;
  offline_seconds?: number | null;
  last_acknowledged_at?: string | null;
  /** When the agent last made contact — its heartbeat or a batch. */
  last_seen_at?: string | null;
  days_pending_rebuild?: number;
  days_awaiting_identity?: number;
  unmatched_punches?: number;
  /** Punches the connector sent that could not be accepted (kept for repair). */
  rejected_punches?: number;
  /** History that has arrived: the oldest and newest punch held. */
  history?: { oldest_punch_at?: string | null; newest_punch_at?: string | null; punches?: number } | null;
  /** The history import's progress; null when it has never run. */
  backfill?: {
    accepted?: number; duplicates?: number; last_acknowledged_at?: string | null; last_seen_at?: string | null;
    /** Set when the agent said the whole history has been imported. */
    completed_at?: string | null;
  } | null;
  /** BioTime's own per-day totals vs what KaziPeople holds (last 14 days);
   *  null until the agent has reported any. */
  reconciliation?: {
    days_checked: number;
    days_short: Array<{ date: string; device: number; held: number; rejected: number; short: number }>;
    last_reported_at: string;
  } | null;
  /** Problems the hourly connector check has open (emailed to attendance contacts). */
  alerts?: Array<{ kind: 'offline' | 'backfill_stalled' | 'codes_waiting' | string; opened_at: string; detail?: Record<string, unknown> }>;
  total_accepted?: number;
  total_duplicates?: number;
}

export interface HealthView {
  tone: Tone;
  /** Short, plain, and never falsely reassuring. */
  headline: string;
  /** What is actually happening. */
  detail: string;
  /** What the reader should do, or empty when there is nothing to do. */
  action: string;
  /** Whether attendance on screen can be trusted as current. */
  trustworthy: boolean;
}

/** "3 minutes", "2 hours", "4 days" — enough precision to judge, no more. */
export function humanDuration(seconds: number): string {
  const unit = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
  const secs = Math.max(1, Math.round(seconds));
  if (secs < 60) return unit(secs, 'second');
  const mins = Math.round(secs / 60);
  if (mins < 60) return unit(mins, 'minute');
  const hours = Math.round(mins / 60);
  if (hours < 24) return unit(hours, 'hour');
  return unit(Math.round(hours / 24), 'day');
}

/**
 * ⚠️ AN UNREACHABLE STATUS IS NOT A HEALTHY ONE. If the status request itself
 * fails, the honest answer is "we do not know", never a blank card and never
 * an optimistic default — the card exists precisely for the case where
 * everything looks normal and is not.
 */
export function unknownHealth(): HealthView {
  return {
    tone: 'warning',
    headline: 'Connector status unavailable',
    detail: 'KaziPeople could not reach the attendance service, so the state of '
      + 'the BioTime connector is unknown. Attendance already recorded is '
      + 'unaffected.',
    action: 'Reload in a moment. If it persists, contact support.',
    trustworthy: false,
  };
}

export function describeHealth(s: ConnectorStatus | null | undefined): HealthView {
  if (!s) return unknownHealth();

  const pending = s.days_pending_rebuild ?? 0;
  const identity = s.days_awaiting_identity ?? 0;
  const unmatched = s.unmatched_punches ?? 0;
  const offline = s.offline_seconds ?? 0;

  switch (s.state) {
    case 'never_connected':
      return {
        tone: 'neutral',
        headline: 'Not connected yet',
        detail: 'The connector has never sent attendance. Nothing is wrong with '
          + 'your data — this integration simply has not started.',
        action: 'Install the agent on the BioTime machine and enrol it.',
        trustworthy: false,
      };

    case 'offline':
      return {
        tone: 'danger',
        headline: `Offline for ${humanDuration(offline)}`,
        // ⚠️ Say what it MEANS, not just that it happened. "Offline" sounds
        // technical; "attendance since then is missing" is the fact.
        // ⚠️ No promise we can't keep: how long a terminal holds scans depends
        // on the device and is not verified here. Say what is TRUE.
        detail: 'No attendance has arrived since then, so what you see here is out of date. '
          + 'Fingerprint terminals normally keep their scans and send them once the connector '
          + 'is back — until then, a missing scan is not an absence.',
        action: 'Check that the BioTime computer is switched on and online.',
        trustworthy: false,
      };

    case 'catching_up':
      return {
        tone: 'warning',
        headline: 'Catching up',
        detail: `${pending} day${pending === 1 ? '' : 's'} still being recalculated `
          + 'after a batch of punches arrived. This finishes on its own.',
        action: '',
        trustworthy: false,
      };

    case 'awaiting_identity':
    case 'degraded':
      return {
        tone: 'warning',
        headline: 'Some punches have no employee',
        // ⚠️ This one will NOT clear by itself, and saying so is the
        // difference between a queue someone works and a number that sits
        // there for months.
        detail: `${unmatched} punch${unmatched === 1 ? '' : 'es'} came from device `
          + `codes KaziPeople does not recognise${identity
            ? `, holding up ${identity} day${identity === 1 ? '' : 's'}` : ''}. `
          + 'They are kept in full, but they are not counted for anyone until '
          + 'someone says who they belong to. This will not resolve on its own.',
        action: 'Match the unrecognised device codes to employees.',
        trustworthy: false,
      };

    case 'synced':
      return {
        tone: 'good',
        headline: 'Attendance is current',
        detail: 'Recent punches have arrived and every day has been calculated. '
          + 'Totals have not been checked against BioTime itself.',
        action: 'Run a reconciliation to confirm nothing is missing.',
        trustworthy: true,
      };

    case 'verified':
      return {
        tone: 'good',
        headline: 'Current and reconciled',
        detail: 'Recent punches have arrived, every day has been calculated, and '
          + 'the totals match BioTime for the checked period.',
        action: '',
        trustworthy: true,
      };

    default:
      // ⚠️ A state we do not recognise is not a state we can vouch for.
      return unknownHealth();
  }
}

export interface ReconcileSummary {
  reconciled: boolean;
  missing_from_kazipeople: number;
  unexpected_in_kazipeople: number;
  days_compared: number;
}

/**
 * ⚠️ BOTH DIRECTIONS MATTER AND THEY MEAN DIFFERENT THINGS. Fewer on our side
 * is a punch that never arrived. MORE on our side means either BioTime
 * deleted something or we are counting something it is not — and that is the
 * direction nobody thinks to check, so it gets said out loud.
 */
export function describeReconciliation(r: ReconcileSummary | null): HealthView {
  if (!r) return unknownHealth();
  if (r.days_compared === 0) {
    return {
      tone: 'warning',
      headline: 'Nothing to compare',
      detail: 'Neither KaziPeople nor BioTime reported any punches for this '
        + 'period. That is not the same as "no differences".',
      action: 'Check the period, and that the terminals were in use.',
      trustworthy: false,
    };
  }
  if (r.reconciled) {
    return {
      tone: 'good',
      headline: `Reconciled across ${r.days_compared} day${r.days_compared === 1 ? '' : 's'}`,
      detail: 'Every day matches the totals BioTime holds.',
      action: '',
      trustworthy: true,
    };
  }
  const parts: string[] = [];
  if (r.missing_from_kazipeople > 0) {
    parts.push(`${r.missing_from_kazipeople} punch`
      + `${r.missing_from_kazipeople === 1 ? '' : 'es'} BioTime has that KaziPeople does not`);
  }
  if (r.unexpected_in_kazipeople > 0) {
    parts.push(`${r.unexpected_in_kazipeople} KaziPeople has that BioTime does not`);
  }
  return {
    tone: 'danger',
    headline: 'Totals do not match',
    detail: `${parts.join(', and ')}. Nothing has been changed on either side.`,
    action: 'Review the differing days before using this period for payroll.',
    trustworthy: false,
  };
}

/**
 * Whether the tenant still has to decide how night shifts are attributed.
 *
 * ⚠️ A SHIFT FROM 22:00 TO 06:00 BELONGS TO ONE DAY OR THE OTHER, and which
 * one decides what gets paid against which day, which week it falls in, and
 * which day reads as absent. There is no safe default, so nothing is assumed
 * — but a decision that is never surfaced is a decision made by accident, and
 * it stays wrong every night somebody works.
 */
export function overnightDecisionPending(opts: {
  overnightMode?: string | null;
  overnightDaysDetected: number;
}): boolean {
  return opts.overnightDaysDetected > 0
    && (!opts.overnightMode || opts.overnightMode === 'split');
}

// ---- Enrolment ---------------------------------------------------------------

/** Where our customers' terminals usually are — listed first. */
export const COMMON_TERMINAL_TIMEZONES = [
  'Africa/Dar_es_Salaam', 'Africa/Nairobi', 'Africa/Kampala', 'Africa/Kigali',
  'Africa/Lusaka', 'Africa/Johannesburg', 'Africa/Lagos', 'Africa/Accra',
];

export interface TimezoneOption { value: string; label: string }

/**
 * ⚠️ A CHOICE, NOT A TEXT BOX. People type what they call their timezone —
 * "EAT", "GMT+3", "Tanzania" — and none of those is an IANA name the server
 * can use, so a free-text field is refused at the worst moment. Every option
 * here is a real IANA zone, labelled the way a person recognises it:
 * "Dar es Salaam — East Africa Time (UTC+03:00)".
 */
export function terminalTimezoneOptions(at: Date = new Date()): TimezoneOption[] {
  let all: string[] = [];
  try {
    all = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.('timeZone') ?? [];
  } catch { /* older browser: the common list alone */ }
  const rest = all.filter((z) => !COMMON_TERMINAL_TIMEZONES.includes(z)).sort();
  return [...COMMON_TERMINAL_TIMEZONES, ...rest].map((value) => ({ value, label: timezoneLabel(value, at) }));
}

export function timezoneLabel(zone: string, at: Date = new Date()): string {
  const city = (zone.split('/').pop() || zone).replace(/_/g, ' ');
  const part = (style: 'long' | 'longOffset') => {
    try {
      return new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: style })
        .formatToParts(at).find((p) => p.type === 'timeZoneName')?.value ?? '';
    } catch {
      return '';
    }
  };
  const name = part('long');
  const offset = part('longOffset').replace(/^GMT$/, 'UTC+00:00').replace(/^GMT/, 'UTC');
  return [city, name && `— ${name}`, offset && `(${offset})`].filter(Boolean).join(' ');
}

/** True only for a real IANA zone name (not "EAT", "GMT+3" or "Tanzania"). */
export function isIanaTimezone(zone: string): boolean {
  if (!zone.includes('/') && zone !== 'UTC') return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

export interface EnrolCheck {
  /** Reasons the form cannot be submitted. Empty = ready. */
  blockers: string[];
  /** True when the go-live date is in the past and must be acknowledged. */
  retroactive: boolean;
}

/**
 * ⚠️ THE GO-LIVE DATE IS THE MOST CONSEQUENTIAL SETTING ON THIS SCREEN.
 * Nothing before it is measured, notified on, or counted toward an HR action.
 * A date in the past means people are measured for days nobody told them
 * about — so it is allowed (a late enrolment is real) but never silently:
 * the reader must say they mean it. No default timezone and no default date:
 * both are facts about the customer, and a guess is wrong in a way that
 * looks right.
 */
export function checkEnrolment(input: {
  timezone: string;
  goLive: string; // YYYY-MM-DD
  today: string; // YYYY-MM-DD, in the viewer's calendar
  retroactiveAcknowledged: boolean;
}): EnrolCheck {
  const blockers: string[] = [];
  if (!input.timezone.trim()) blockers.push('Choose the timezone the fingerprint terminals are in.');
  else if (!isIanaTimezone(input.timezone.trim())) blockers.push('Choose the timezone from the list.');
  const validDate = /^\d{4}-\d{2}-\d{2}$/.test(input.goLive);
  if (!validDate) blockers.push('Choose the go-live date.');
  const retroactive = validDate && input.goLive < input.today;
  if (retroactive && !input.retroactiveAcknowledged) {
    blockers.push('Confirm that days before today should be measured.');
  }
  return { blockers, retroactive };
}

// ---- Installer download ------------------------------------------------------

export type InstallerArch = 'x64' | 'x86';

/** Built into dist/downloads by CI (both architectures) with this environment's API address. */
export const installerPath = (arch: InstallerArch) => `/downloads/kazipeople-biotime-agent-${arch}.exe`;

/**
 * Which build THIS computer needs. 64-bit Windows → x64. Everything else —
 * 32-bit Windows, ARM, an old browser, or downloading on a Mac to copy across —
 * → x86, because the 32-bit build runs on every Windows: a wrong guess must
 * never produce a file that will not start.
 *
 * Signals, strongest first: Chromium's high-entropy client hints (bitness +
 * architecture), then the user-agent string (Win64/x64, or WOW64 = a 32-bit
 * browser on 64-bit Windows).
 */
/** Whether the visitor is on Windows — the only place the installer runs. */
export function isWindowsClient(input: { platform?: string; userAgent?: string }): boolean {
  return /windows/i.test(input.platform ?? '') || /Windows NT/i.test(input.userAgent ?? '');
}

export function pickInstallerArch(input: {
  platform?: string; // userAgentData.platform
  bitness?: string; // userAgentData high-entropy "bitness"
  architecture?: string; // userAgentData high-entropy "architecture"
  userAgent?: string;
}): InstallerArch {
  const ua = input.userAgent ?? '';
  if (!isWindowsClient(input)) return 'x86';
  if (input.architecture && input.architecture.toLowerCase().startsWith('arm')) return 'x86';
  if (input.bitness === '64') return 'x64';
  if (input.bitness === '32') return 'x86';
  if (/Win64|x64|WOW64|amd64/i.test(ua)) return 'x64';
  return 'x86';
}

/**
 * The saved file's name carries the one-time code; the agent reads it back
 * with the same pattern (cmd/kazipeople-biotime-agent/wizard.go,
 * codeFromFilename). Keep the two in step.
 */
export function installerFileName(code: string): string {
  return `KaziPeople-BioTime-Installer-${code.toUpperCase()}.exe`;
}
